/**
 * CML_POPUP Vuex module — driver for the marker-popup-card action buttons.
 *
 * Wave C-α (specs/003-cml-phase2-popup-cards). T016 / T018 / T019.
 *
 * The popup itself manages its own open/close/data state via the
 * `Vue.observable` singleton in `@/composables/usePopupCard.js`. This module
 * exists separately to host the **action-button outbound flows** — the
 * orchestration that fires when the user clicks View Detail / Add Reading /
 * Edit on an open popup. These flows reach into the broader UI (router,
 * existing dialogs in `inspectionEdit.vue`, etc.) and benefit from Vuex's
 * action/mutation discipline.
 *
 * MVP scope note (Wave C-α, 2026-05-16):
 *   The full integration of these three actions with the existing
 *   inspection-context UI (CmlEdit dialog, Analysis CML detail tab,
 *   Add Reading flow) requires architectural decisions that depend on the
 *   broader product flow — see tasks.md §U2 risk note for T016. For MVP
 *   ship, each action:
 *     • Sets a request state (cmlId + timestamp) so downstream UIs can react.
 *     • Surfaces a toast confirming the click was wired through correctly.
 *     • Logs the request for debugging.
 *
 *   Downstream UI watchers (e.g., inspectionEdit.vue when present) can read
 *   the request state and trigger the appropriate dialog/route. When the
 *   inspection context is *not* active (popup opened from a stand-alone 3D
 *   view, no parent inspection mounted), the toast informs the user. The
 *   follow-up PR that fully wires these flows will replace the toast
 *   fallback with the actual dialog/route handoff.
 */

import Vue from 'vue';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
import { TAG_INFO_TAB } from '@/constants/tabs';
import { monitoringTabState } from '@/features/Tags/components/monitoring/useMonitoringTab';

/**
 * Resolve the master-tag context for a popup view-model (AVA-437 Tier 2).
 * Works for both the CML popup (entityId = cmlId; masterTagId from the vm or the
 * Monitoring cache) and the TmlPoint popup (entityId = tmlPointId; the master tag
 * is resolved THROUGH the parent CML — vm.cmlRecordId — since a TmlPoint carries
 * no masterTagId of its own).
 * @returns {{ masterTagId: string|null, cmlRecordId: string|null, tmlPointId: string|null }}
 */
function resolveMasterContext(vm, entityType, entityId) {
  const isTml = entityType === 'tmlpoint';
  const cmlRecordId = isTml ? ((vm && vm.cmlRecordId) || null) : entityId;
  const tmlPointId = isTml ? entityId : null;

  let masterTagId = (!isTml && vm && vm.masterTagId) || null;
  if (!masterTagId && cmlRecordId) {
    // eslint-disable-next-line no-restricted-syntax
    for (const mtId of Object.keys(monitoringTabState.cmlsByMasterTag)) {
      const list = monitoringTabState.cmlsByMasterTag[mtId] || [];
      if (list.some((c) => c && c.id === cmlRecordId)) { masterTagId = mtId; break; }
    }
  }
  return { masterTagId, cmlRecordId, tmlPointId };
}

export const CML_POPUP = {
  namespaced: true,

  state: {
    // Each request carries an autoincremented `requestId` so a watcher can
    // distinguish "user clicked Edit again on the same CML" from "no change."
    // Without an id, two clicks in a row on the same cmlId wouldn't trigger
    // a re-watch in Vuex.
    editRequest: null,        // { cmlId, vm, requestId } | null
    viewDetailRequest: null,  // { cmlId, vm, requestId } | null
    addReadingRequest: null,  // { cmlId, vm, requestId } | null

    // AVA-437 #5a — true while REQUEST_VIEW_DETAIL is awaiting GET_BASE_TAG_BY_ID
    // on the registry-miss path. Drives a spinner on the popup's "View readings"
    // button so the ~2s fetch gives the user feedback instead of feeling dead.
    viewDetailLoading: false,
  },

  getters: {
    // Convenience getters for components that watch for incoming requests.
    latestEditRequest: (state) => state.editRequest,
    latestViewDetailRequest: (state) => state.viewDetailRequest,
    latestAddReadingRequest: (state) => state.addReadingRequest,
    isViewDetailLoading: (state) => state.viewDetailLoading,
  },

  mutations: {
    SET_EDIT_REQUEST(state, payload) {
      state.editRequest = payload;
    },
    SET_VIEW_DETAIL_REQUEST(state, payload) {
      state.viewDetailRequest = payload;
    },
    SET_VIEW_DETAIL_LOADING(state, v) {
      state.viewDetailLoading = v;
    },
    SET_ADD_READING_REQUEST(state, payload) {
      state.addReadingRequest = payload;
    },
    CLEAR_ALL_REQUESTS(state) {
      state.editRequest = null;
      state.viewDetailRequest = null;
      state.addReadingRequest = null;
    },
  },

  actions: {
    /**
     * T016 — Open the CmlEdit dialog for a popup-driven Edit click.
     *
     * The CmlEdit.vue component is currently mounted as a child of
     * inspectionEdit.vue (line 208) with v-model="showCmlDialog". When the
     * popup is open in an *inspection viewing context*, a watcher in
     * inspectionEdit.vue (added in a follow-up PR) can read editRequest and
     * open the dialog with the right cml/index. When the popup is open in a
     * *stand-alone 3D viewer context*, no inspectionEdit is mounted and the
     * Edit click surfaces a toast directing the user to the inspection.
     *
     * Acceptance: This action satisfies FR-007 by recording the intent in a
     * way the existing UI can react to. The wire-through to the actual
     * dialog open happens via downstream watchers — those land in the
     * follow-up PR that also addresses the U2 risk note in tasks.md.
     */
    REQUEST_EDIT({ commit, dispatch, state }, payload) {
      // AVA-437 Tier 2 — accept { entityType, entityId, vm }; entityId is a cmlId (CML
      // popup) or a tmlPointId (TML popup). Back-compat with the legacy { cmlId, vm }.
      const cmlId = (payload && (payload.entityId || payload.cmlId)) || null;
      const vm = payload && payload.vm;
      if (!cmlId) return;
      const requestId = (state.editRequest?.requestId || 0) + 1;
      debugLog(`[CML_POPUP] REQUEST_EDIT cmlId=${cmlId} requestId=${requestId}`);
      commit('SET_EDIT_REQUEST', { cmlId, vm, requestId });

      // MVP toast fallback. When inspectionEdit.vue's watcher is wired in
      // the follow-up PR, it can intercept the request and suppress this
      // toast for the in-context case.
      dispatch(
        'TOAST/INFO',
        {
          message: `Edit CML ${vm?.cmlNo || cmlId} — opening the editor (open the inspection if the dialog doesn't appear).`,
          options: { timeout: 3000 },
        },
        { root: true },
      );
    },

    /**
     * View Detail — open the master tag info panel for the CML's parent
     * Master Tag, switched to the Monitoring tab, and highlight the
     * clicked CML's row.
     *
     * Why this destination (not a dedicated CML route):
     *   The "Integrity Hub CML detail" referenced in FR-006 isn't a separate
     *   route in the current product — CML detail is rendered inside the
     *   Master Tag info panel's Monitoring tab (Wave 005). Opening that
     *   panel with the right tab + the CML selected delivers what View
     *   Detail should mean (full reading history, TmlPoints expanded, edit
     *   affordances) without inventing a separate page.
     *
     * Resolution path (cache-first, no network):
     *   1. vm.masterTagId is set by buildCmlPopupViewModel from the popup
     *      fetch — use it directly when present.
     *   2. Fallback: scan monitoringTabState.cmlsByMasterTag for the cmlId
     *      and read its masterTagId from there (the cache the monitoring
     *      tab already populated on first open).
     *   3. Last-resort fallback: toast that we can't navigate without
     *      master tag context (defensive — shouldn't happen in practice
     *      because the popup itself requires the same data).
     *
     * Selection highlight:
     *   commit('SET_SELECTED_CML_ID') so CmlRecordRow / MonitoringTab
     *   consumers can react to the selection (scroll into view, expand,
     *   apply a highlight class). The mutation already exists from Wave C-α
     *   wiring — components that subscribe to it can land in a follow-up
     *   without blocking the navigation itself.
     */
    async REQUEST_VIEW_DETAIL({ commit, dispatch, state, rootState }, payload) {
      // AVA-437 Tier 2 — both popups route here via { entityType, entityId, vm }.
      // entityId is a cmlId (CML popup) or a tmlPointId (TML popup). Back-compat with
      // the legacy { cmlId, vm } shape.
      const entityType = (payload && payload.entityType) || 'cml';
      const entityId = (payload && (payload.entityId || payload.cmlId)) || null;
      const vm = payload && payload.vm;
      if (!entityId) return;
      const requestId = (state.viewDetailRequest?.requestId || 0) + 1;
      debugLog(`[CML_POPUP] REQUEST_VIEW_DETAIL entityType=${entityType} entityId=${entityId} requestId=${requestId}`);
      commit('SET_VIEW_DETAIL_REQUEST', { cmlId: entityId, entityType, vm, requestId });

      // AVA-437 #5a — show the spinner immediately so the ~2s GET_BASE_TAG_BY_ID
      // wait on the registry-miss path isn't dead time. Cleared in `finally` on
      // EVERY exit path below.
      commit('SET_VIEW_DETAIL_LOADING', true);

      try {
        // Resolve the master-tag context. For a TmlPoint this walks the parent CML
        // (vm.cmlRecordId) in the Monitoring cache, since a TmlPoint has no masterTagId.
        const { masterTagId, cmlRecordId, tmlPointId } = resolveMasterContext(vm, entityType, entityId);

        if (!masterTagId) {
          debugWarn(`[CML_POPUP] REQUEST_VIEW_DETAIL entityId=${entityId} — masterTagId unknown, can't open parent panel`);
          dispatch(
            'TOAST/WARNING',
            {
              message: 'Could not find this CML\'s master tag. Open the parent panel manually.',
              options: { timeout: 3000 },
            },
            { root: true },
          );
          return;
        }

        // Look up the master tag instance from forgeService — OPEN_TAG needs
        // a tag-class instance (Equipment / Pipeline / Structural / Annotation),
        // not just an ID. getTagById finds the existing in-memory tag.
        const forgeService = rootState.forgeService;
        let masterTag = forgeService && typeof forgeService.getTagById === 'function'
          ? forgeService.getTagById(masterTagId)
          : null;

        // Fallback (AVA-437 #5): the master tag may not be in the in-memory tag
        // registry when the popup was opened from a viewer/context that never
        // harvested it. Rather than dead-ending with a warning, fetch the
        // tag-class instance from the server. GET_BASE_TAG_BY_ID resolves a proper
        // ForgeEquipment/ForgePipeline/… instance — the same shape getTagById returns.
        if (!masterTag) {
          debugLog(`[CML_POPUP] REQUEST_VIEW_DETAIL — master tag ${masterTagId} not in registry; fetching via GET_BASE_TAG_BY_ID`);
          try {
            masterTag = await dispatch('GET_BASE_TAG_BY_ID', masterTagId, { root: true });
          } catch (err) {
            debugError(`[CML_POPUP] REQUEST_VIEW_DETAIL — GET_BASE_TAG_BY_ID failed for ${masterTagId}`, err);
            masterTag = null;
          }
        }

        // The view-detail request may have been superseded while the GET_BASE_TAG_BY_ID
        // fetch was in flight (user clicked View Detail on another CML). Check staleness
        // BEFORE the failure toast below, so a stale request that ALSO failed its fetch
        // aborts SILENTLY instead of flashing the misleading "not loaded" toast. Honour
        // only the latest request. (No-op on the registry-hit path — no await occurred.)
        if (state.viewDetailRequest && state.viewDetailRequest.requestId !== requestId) {
          debugLog(`[CML_POPUP] REQUEST_VIEW_DETAIL requestId=${requestId} superseded; aborting stale open`);
          return;
        }

        if (!masterTag) {
          debugWarn(`[CML_POPUP] REQUEST_VIEW_DETAIL — master tag ${masterTagId} unavailable (registry miss + fetch failed); aborting`);
          dispatch(
            'TOAST/WARNING',
            {
              message: 'The parent master tag isn\'t loaded in this view. Try opening the tag from the tag list.',
              options: { timeout: 3000 },
            },
            { root: true },
          );
          return;
        }

        // Mark which CML should be highlighted/scrolled-to in the Monitoring tab.
        // Downstream consumers (CmlRecordRow watcher landing in a follow-up) can
        // read this to scroll into view + auto-expand. Setting it BEFORE the
        // OPEN_TAG dispatch so by the time the tab mounts, the selection is in
        // place.
        commit('SET_SELECTED_CML_ID', cmlRecordId, { root: true });
        // TML popup → also flag the TmlPoint row; CML popup → null clears any stale TML highlight.
        commit('SET_SELECTED_TML_POINT_ID', tmlPointId, { root: true });

        // AVA-437 #5b — the popup INTENTIONALLY stays open. OPEN_TAG opens the
        // master panel beside (not over) the popup; the redundant CML marker
        // re-render the panel's mixin triggers is now skipped in forgeService
        // (`showCmlPointMarkers` identical-set guard) so the popup's anchor
        // survives. (Removed the previous explicit closePopupCard() here.)

        // Open the master tag panel on the Monitoring tab. .clone() matches
        // the convention used by every other OPEN_TAG caller (linkedTags.vue,
        // CorrosionAnalyser.vue, etc.) — avoids mutating the in-place tag.
        dispatch(
          'OPEN_TAG',
          {
            tag: typeof masterTag.clone === 'function' ? masterTag.clone() : masterTag,
            clearPrevious: false,
            defaultTab: TAG_INFO_TAB.MONITORING,
          },
          { root: true },
        );

        debugLog(`[CML_POPUP] View Detail → opened master tag ${masterTagId} on Monitoring tab, selectedCmlId=${cmlRecordId}, selectedTmlPointId=${tmlPointId}`);
      } finally {
        // Clear the spinner. Guard against a superseded request clobbering a
        // newer one's flag: only the request that is still current may reset it.
        if (state.viewDetailRequest && state.viewDetailRequest.requestId === requestId) {
          commit('SET_VIEW_DETAIL_LOADING', false);
        }
      }
    },

    /**
     * T019 — Open the Add Reading entry flow for a popup-driven Add Reading
     * click. The existing Add Reading UI lives inside the inspection
     * editor (a CML in an inspection's CML list has an "Add Reading"
     * affordance). Without an inspection context, the popup's Add Reading
     * surfaces a toast directing the user; the follow-up PR routes to the
     * latest inspection that owns the CML, then triggers the existing flow.
     */
    REQUEST_ADD_READING({ commit, dispatch, state }, payload) {
      // AVA-437 Tier 2 — accept { entityType, entityId, vm } (entityId = cmlId or tmlPointId);
      // back-compat with the legacy { cmlId, vm }.
      const cmlId = (payload && (payload.entityId || payload.cmlId)) || null;
      const vm = payload && payload.vm;
      if (!cmlId) return;
      const requestId = (state.addReadingRequest?.requestId || 0) + 1;
      debugLog(`[CML_POPUP] REQUEST_ADD_READING cmlId=${cmlId} requestId=${requestId}`);
      commit('SET_ADD_READING_REQUEST', { cmlId, vm, requestId });

      dispatch(
        'TOAST/INFO',
        {
          message: `Add reading for CML ${vm?.cmlNo || cmlId} — opening the entry flow.`,
          options: { timeout: 3000 },
        },
        { root: true },
      );
    },
  },
};

// Re-export Vue so consumers don't have to import it separately if they
// want a single import point for the module. (Vue is used by Vue.observable
// elsewhere in this codebase; keeping the pattern consistent.)
export default { Vue };
